import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "";
  }
};

const PlaylistSection = () => {
  const [songTitle, setSongTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSongs = async () => {
    try {
      const { data } = await axios.get(`${API}/playlist`);
      setSongs(data);
    } catch {
      // silent — the form still works
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSongs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!songTitle.trim() || !artist.trim()) {
      toast.error("Añade al menos el título y el artista.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/playlist`, {
        song_title: songTitle.trim(),
        artist: artist.trim(),
        message: message.trim() || null,
      });
      toast.success("¡Gracias! Tu canción ya está en la lista.");
      setSongTitle("");
      setArtist("");
      setMessage("");
      fetchSongs();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(detail || "Esa canción ya la ha propuesto alguien.");
      } else {
        toast.error("No pudimos guardar tu canción. Prueba otra vez en un momento.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="playlist"
      data-testid="playlist-section"
      className="border-t border-[#c9c3b3] py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-start justify-between">
          <p className="corner-label text-[#8a8580]">(08) — Wedding playlist</p>
        </div>

        <div className="mt-16 grid grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-7"
          >
            <h2 className="display-xl">
              Ayúdanos a montar
              <br />
              <span className="italic-serif">nuestra playlist.</span>
            </h2>
          </motion.div>
          <div className="col-span-12 md:col-span-5 md:pt-8">
            <p className="body-lead max-w-md">
              Toda gran fiesta necesita gran música. Sugerid una canción que os
              apetezca bailar y ayudadnos a crear una playlist llena de
              recuerdos, placeres culpables y temas para bailar sin parar.
            </p>
          </div>
        </div>

        {/* Form + list — two columns on desktop, stacked on mobile */}
        <div className="mt-16 grid grid-cols-12 gap-10 md:gap-14">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            onSubmit={handleSubmit}
            className="col-span-12 md:col-span-6"
            data-testid="playlist-form"
          >
            <div className="border border-[#c9c3b3] bg-[#f2ede2] p-8 md:p-10">
              <p className="corner-label text-[#8a8580]">Propón una canción</p>

              <div className="mt-6">
                <label
                  htmlFor="song-title"
                  className="corner-label text-[#0a0a0a]"
                >
                  Título de la canción *
                </label>
                <input
                  id="song-title"
                  data-testid="playlist-song-title"
                  type="text"
                  value={songTitle}
                  onChange={(e) => setSongTitle(e.target.value)}
                  maxLength={200}
                  required
                  placeholder="Ej. Baila Morena"
                  className="mt-2 block w-full border-b border-[#c9c3b3] bg-transparent py-2 text-lg outline-none transition focus:border-[#0a0a0a] placeholder:text-[#8a8580]/70"
                />
              </div>

              <div className="mt-6">
                <label htmlFor="artist" className="corner-label text-[#0a0a0a]">
                  Artista *
                </label>
                <input
                  id="artist"
                  data-testid="playlist-artist"
                  type="text"
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  maxLength={200}
                  required
                  placeholder="Ej. Zucchero"
                  className="mt-2 block w-full border-b border-[#c9c3b3] bg-transparent py-2 text-lg outline-none transition focus:border-[#0a0a0a] placeholder:text-[#8a8580]/70"
                />
              </div>

              <div className="mt-6">
                <label htmlFor="message" className="corner-label text-[#0a0a0a]">
                  Mensaje opcional
                </label>
                <textarea
                  id="message"
                  data-testid="playlist-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="¿Por qué esta canción? / Me recuerda a…"
                  className="mt-2 block w-full resize-none border-b border-[#c9c3b3] bg-transparent py-2 text-base outline-none transition focus:border-[#0a0a0a] placeholder:text-[#8a8580]/70"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="playlist-submit"
                className="btn-base btn-primary mt-8"
              >
                <span>
                  {submitting ? "Enviando…" : "Añadir a la playlist"}
                </span>
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </motion.form>

          {/* List */}
          <div className="col-span-12 md:col-span-6" data-testid="playlist-list">
            <div className="mb-4 flex items-baseline justify-between">
              <p className="corner-label text-[#8a8580]">
                Canciones sugeridas
              </p>
              <p className="corner-label text-[#8a8580]">
                {String(songs.length).padStart(2, "0")}
              </p>
            </div>

            {loading ? (
              <p className="body-lead text-[#8a8580]">Cargando…</p>
            ) : songs.length === 0 ? (
              <div className="border border-dashed border-[#c9c3b3] p-8 text-center">
                <p className="italic-serif text-xl">
                  Aún no hay canciones. Sé la primera persona en sugerir una.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-[#c9c3b3] border-t border-[#c9c3b3]">
                <AnimatePresence initial={false}>
                  {songs.map((s) => (
                    <motion.li
                      key={s.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="py-5"
                      data-testid={`playlist-item-${s.id}`}
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div className="min-w-0 flex-1">
                          <p className="display-md leading-tight">
                            <span aria-hidden="true">🎵 </span>
                            {s.song_title}
                          </p>
                          <p className="mt-1 body-lead text-[#3a3a3a]">
                            <span aria-hidden="true">🎤 </span>
                            {s.artist}
                          </p>
                          {s.message && (
                            <p className="mt-3 italic-serif text-[#3a3a3a]">
                              <span aria-hidden="true">💬 </span>
                              {s.message}
                            </p>
                          )}
                        </div>
                        <span className="shrink-0 corner-label text-[#8a8580]">
                          {formatDate(s.created_at)}
                        </span>
                      </div>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlaylistSection;
